import React from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'

import type { HolidayNavTab } from '@/src/types/holidays'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface HolidayNavTabsProps {
    tabs: HolidayNavTab[]
    activeId: string
    onTabClick: (id: string) => void
    isMobile: boolean
    /** Few enough tabs to spread edge to edge; otherwise they scroll. */
    spread: boolean
}

const HolidayNavTabs: React.FC<HolidayNavTabsProps> = ({ tabs, activeId, onTabClick, isMobile, spread }) => {
    return (
        /*
         * Stickiness belongs to the page; the card itself belongs here.
         *
         * Radius and shadow both come from the SRP "modify search" bar, so the two read as the same
         * surface. The radius is the shared `HOLIDAYS_RADIUS.SURFACE` token rather than a local
         * number, which keeps this bar, the content card and the enquiry card on one corner.
         */
        <Box
            sx={(theme: Theme) => ({
                bgcolor: 'background.paper',
                borderRadius: HOLIDAYS_RADIUS.SURFACE,
                border: '1px solid',
                borderColor: 'borders.default',
                boxShadow: `0 1px 2px ${alpha(theme.palette.neutral.black, 0.05)}`,
                px: isMobile ? 1 : 3
            })}
        >
            <Tabs
                value={activeId}
                onChange={(_event, value: string) => onTabClick(value)}
                variant={spread ? 'fullWidth' : 'scrollable'}
                scrollButtons={spread ? false : 'auto'}
                allowScrollButtonsMobile
                sx={{
                    minHeight: isMobile ? 48 : 60,
                    // The active tab is carried by colour alone, matching the reference design.
                    '& .MuiTabs-indicator': { display: 'none' },
                    // MUI hides a disabled scroll arrow with opacity 0, leaving a blank gap; show it dimmed.
                    '& .MuiTabScrollButton-root': { width: isMobile ? 24 : 32 },
                    '& .MuiTabScrollButton-root.Mui-disabled': { opacity: 0.3 }
                }}
            >
                {tabs.map(tab => (
                    <Tab
                        key={tab.id}
                        value={tab.id}
                        label={tab.label}
                        sx={{
                            minHeight: isMobile ? 48 : 60,
                            px: isMobile ? 1.5 : 2,
                            fontSize: isMobile ? '1rem' : '0.938rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            color: 'textColors.subtitle',
                            '&.Mui-selected': { color: 'primary.main', fontWeight: 700 }
                        }}
                    />
                ))}
            </Tabs>
        </Box>
    )
}

export { HolidayNavTabs }
export type { HolidayNavTabsProps }
export default HolidayNavTabs
